import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Ruler, Check, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { SHAPES, shapeArea } from './shapes';
import { ShapeRow } from './ShapeRow';
import { formatAUD } from '../CartContext';

let nextId = 1;
const newSection = (type = 'rectangle') => ({ id: nextId++, type, values: { ...SHAPES[type].defaults } });

export function AreaCalculatorDialog({ open, onOpenChange, onUse, varieties }) {
  const navigate = useNavigate();
  const [sections, setSections] = useState([newSection()]);
  const [waste, setWaste] = useState(true);
  const [picking, setPicking] = useState(false);

  useEffect(() => { if (open) { setSections([newSection()]); setWaste(true); setPicking(false); } }, [open]);

  const raw = sections.reduce((s, sec) => s + shapeArea(sec.type, sec.values), 0);
  const total = raw >= 0.5 ? Math.ceil(raw * (waste ? 1.05 : 1)) : 0;

  const use = () => {
    if (total <= 0) return;
    if (varieties) { setPicking(true); return; }
    onUse(total);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#F7F4EC] border-[#E9E3D3] !block space-y-4" data-testid="area-calculator">
        <DialogHeader className="pr-8">
          <DialogTitle className="font-heading text-2xl text-[#1c1b17] flex items-center gap-2"><Ruler className="h-6 w-6 text-[#D97757]" /> Work out your lawn area</DialogTitle>
          <p className="text-sm text-[#6b6a62]">Measure each part of your lawn in metres. Add a section for every separate patch — front, back, side strip — and we'll total it for you.</p>
        </DialogHeader>

        {picking ? (
          <div className="space-y-3" data-testid="area-pick-variety">
            <p className="text-sm">You need about <strong className="text-[#1B4332]">{total} m²</strong>. Which turf would you like?</p>
            {varieties.filter((v) => v.in_stock).map((v) => (
              <button key={v.id} type="button" onClick={() => { onOpenChange(false); navigate(`/shop/varieties/${v.slug}?area=${total}`); }} className="w-full flex items-center justify-between gap-3 rounded-xl bg-white border border-[#E9E3D3] px-4 py-3 text-left hover:border-[#40916c] transition-colors" data-testid={`pick-variety-${v.slug}`}>
                <span><span className="font-semibold block">{v.name}</span><span className="text-xs text-[#6b6a62]">{formatAUD(v.price_per_m2)}/m² · est. {formatAUD(v.price_per_m2 * total)}</span></span>
                <ArrowRight className="h-4 w-4 text-[#1B4332]" />
              </button>
            ))}
            <button type="button" onClick={() => setPicking(false)} className="text-sm underline text-[#6b6a62]" data-testid="area-back">Back to measurements</button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {sections.map((sec, i) => (
                <ShapeRow key={sec.id} section={sec} index={i} canRemove={sections.length > 1}
                  onChange={(s) => setSections((list) => list.map((x) => (x.id === sec.id ? s : x)))}
                  onRemove={() => setSections((list) => list.filter((x) => x.id !== sec.id))} />
              ))}
              <button type="button" onClick={() => setSections((l) => [...l, newSection()])} className="flex items-center gap-2 text-sm font-semibold text-[#1B4332] hover:underline py-1" data-testid="add-section"><Plus className="h-4 w-4" /> Add another section</button>
            </div>

            <div className="rounded-xl p-4 text-white flex flex-col sm:flex-row sm:items-center gap-4" style={{ background: '#1B4332' }}>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-white/60">Total turf needed</p>
                <p className="font-heading text-3xl font-bold" data-testid="area-total">{total} m²</p>
                <p className="text-xs text-white/70">{raw.toFixed(1)} m² measured{waste ? ' + 5% for cuts and edges, rounded up' : ', rounded up'}</p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={waste} onChange={(e) => setWaste(e.target.checked)} className="h-4 w-4 accent-[#D97757]" data-testid="waste-toggle" /> Add 5% for cuts &amp; edges
              </label>
              <button type="button" onClick={use} disabled={total <= 0} className="inline-flex items-center justify-center gap-2 font-semibold rounded-full px-6 py-2.5 text-sm text-white transition-transform hover:scale-[1.02] disabled:opacity-50" style={{ background: '#D97757' }} data-testid="use-area-btn"><Check className="h-4 w-4" /> {varieties ? 'Choose my turf' : `Use ${total} m²`}</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
